import { createElement as rc, useCallback } from 'react';
import PropTypes from 'prop-types';
import { View, Text, Switch, styled, fromTheme } from 'lib_ui-primitives';
import { metadata } from 'lib_ui-services';
import useSearchElement from './useSearchElement';
import FormField from '../formElement/FormField';

// Always false so that label floats permanently (instead of overlapping the control).
const fieldEmpty = false;

const SwitchWithMargin = styled(Switch).attrs({ name: 'switch-with-margin' })`
    margin-left: ${fromTheme('textPadding')};
    margin-right: ${fromTheme('textPadding')};
    flex-shrink: 0;
`;
SwitchWithMargin.displayName = 'SwitchWithMargin';

const SwitchContainer = styled(View).attrs({ name: 'switch-container' })`
    padding-left: ${fromTheme('textPadding')};
    padding-top: ${fromTheme('textPadding')};
    margin-top: ${fromTheme('textMargin')};
    align-items: center;
    column-gap: ${({ size = 'sm' }) => fromTheme('switch', 'options', 'gap', size)};
`;
SwitchContainer.displayName = 'SwitchContainer';

const ToggleOptions = styled(Text).attrs({ name: 'toggle-options' })`
    color: ${({ selected, theme }) => {
        return selected ? theme.defaultFontColor : theme.disabledFontColor;
    }};
`;
ToggleOptions.displayName = 'ToggleOptions';

/**
 * IncludedValueSearch renders a 2-state toggle. When true, it restricts results to records
 * whose property value is included in the configured list (values). Optionally includes
 * undefined values when configured to do so. Optionally checks for the existence of the
 * property value when `valueExists` is true.
 */
function IncludedValueSearch(props) {
    const {
        hNode,
        hNode: {
            id,
            title,
            propertyName,
            toggleValues,
            values = [],
            includeUndefined = false,
            valueExists = false,
            size
        },
        disabled,
        filterWrapper = x => x
    } = props || { hNode: {} };

    const propertyPath = metadata.getPathToProperty(hNode);
    const labels = toggleValues ?? (valueExists ? ['Any', 'Exists'] : ['', 'true']);

    // Translate toggle state to a special descriptor consumed by SearchPane's buildSearchCriteria
    const valueToFilter = useCallback(
        state => {
            if (state === true) {
                if (valueExists) {
                    // Simple existence check
                    return filterWrapper({ [propertyPath]: { $exists: true } });
                }
                // Included-value semantics
                return {
                    __includedValueSearch: {
                        propertyPath,
                        values,
                        includeUndefined: !!includeUndefined,
                        filterWrapper
                    }
                };
            }
            // false => clear
            return {};
        },
        [propertyPath, values, includeUndefined, valueExists, filterWrapper]
    );

    // Default to false (off)
    const { value, setValue, active, onFocus, onBlur } = useSearchElement(props, valueToFilter, false);

    // Update the search element value when the toggle state changes
    const onToggle = useCallback(() => {
        setValue(!value);
    }, [value, setValue]);

    // prettier-ignore
    return rc(FormField, { id, hNode, title, active, fieldEmpty },
        rc(SwitchContainer, {size},
            rc(ToggleOptions, { selected: value === false }, labels[0]),
            rc(SwitchWithMargin, { id, value: value, onClick: onToggle, disabled, name: propertyName, onFocus, onBlur }),
            rc(ToggleOptions, { selected: value === true }, labels[1])
        )
    );
}

IncludedValueSearch.propTypes = {
    hNode: PropTypes.shape({
        id: PropTypes.string.isRequired,
        title: PropTypes.string.isRequired,
        propertyName: PropTypes.string.isRequired,
        toggleValues: PropTypes.arrayOf(PropTypes.string),
        values: PropTypes.arrayOf(PropTypes.string),
        includeUndefined: PropTypes.bool,
        valueExists: PropTypes.bool,
        disabled: PropTypes.bool
    }).isRequired
};

export default IncludedValueSearch;
